import { apiDelete, apiGet, apiPatch, apiPost } from './api';

export type TrainingLocaleContent = {
  title?: string;
  subtitle?: string;
  shortDescription?: string;
  longDescription?: string;
};

export type TrainingProgramSummary = {
  _id: string;
  code: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  locales: { en: TrainingLocaleContent; fr: TrainingLocaleContent };
  pillarTags?: string[];
  estimatedDurationMinutes?: number | null;
  levelCount: number;
  lessonCount: number;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TrainingLevel = {
  _id: string;
  formationId: string;
  order: number;
  code: string;
  locales: { en: { title: string; description: string }; fr: { title: string; description: string } };
  icon?: string;
  lessonIds?: string[];
  lessons?: TrainingLesson[];
  unlockRule?: string;
  completionBadgeId?: string | null;
};

export type LessonDocumentRef = {
  _id: string;
  order: number;
  documentId: string;
  documentCode: string;
  locales?: { en?: { navLabel?: string }; fr?: { navLabel?: string } };
  required?: boolean;
  minProgression?: number;
};

export type ValidationStep = {
  _id: string;
  order: number;
  type: string;
  locales?: { en?: { title?: string; instructions?: string }; fr?: { title?: string; instructions?: string } };
  exerciseId?: string | null;
  passScorePercent?: number;
  actionType?: string;
  sandboxOnly?: boolean;
};

export type TrainingLesson = {
  _id: string;
  formationId: string;
  levelId: string;
  order: number;
  code: string;
  locales: {
    en: { title: string; summary: string; objectives: string[] };
    fr: { title: string; summary: string; objectives: string[] };
  };
  pillar?: string | null;
  estimatedMinutes?: number | null;
  documents: LessonDocumentRef[];
  validationSteps: ValidationStep[];
  status?: string;
};

export type FormationTree = {
  program: TrainingProgramSummary & Record<string, unknown>;
  levels: TrainingLevel[];
};

type ApiListResponse = {
  success: boolean;
  data?: { programs: TrainingProgramSummary[]; total: number };
  message?: string;
};

type ApiTreeResponse = {
  success: boolean;
  data?: FormationTree;
  message?: string;
};

type ApiOneResponse<T> = {
  success: boolean;
  data?: T;
  message?: string;
};

export async function listFormations(params: Record<string, string | number> = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();
  return apiGet<ApiListResponse>(`/api/v1/admin/formations${qs ? `?${qs}` : ''}`);
}

export async function createFormation(body: Record<string, unknown>) {
  return apiPost<ApiOneResponse<{ program: TrainingProgramSummary }>>('/api/v1/admin/formations', body);
}

export async function getFormation(formationId: string) {
  return apiGet<ApiTreeResponse>(`/api/v1/admin/formations/${formationId}`);
}

export async function updateFormation(formationId: string, body: Record<string, unknown>) {
  return apiPatch<ApiOneResponse<{ program: TrainingProgramSummary }>>(
    `/api/v1/admin/formations/${formationId}`,
    body,
  );
}

export async function archiveFormation(formationId: string) {
  return apiDelete<ApiOneResponse<{ program: TrainingProgramSummary }>>(
    `/api/v1/admin/formations/${formationId}`,
  );
}

export async function addLevel(formationId: string, body: Record<string, unknown>) {
  return apiPost<ApiOneResponse<{ level: TrainingLevel }>>(
    `/api/v1/admin/formations/${formationId}/levels`,
    body,
  );
}

export async function updateLevel(levelId: string, body: Record<string, unknown>) {
  return apiPatch<ApiOneResponse<{ level: TrainingLevel }>>(
    `/api/v1/admin/formations/levels/${levelId}`,
    body,
  );
}

export async function deleteLevel(levelId: string) {
  return apiDelete<ApiOneResponse<{ deleted: boolean }>>(
    `/api/v1/admin/formations/levels/${levelId}`,
  );
}

export async function reorderLevels(formationId: string, orderedIds: string[]) {
  return apiPatch<ApiOneResponse<{ levels: TrainingLevel[] }>>(
    `/api/v1/admin/formations/${formationId}/levels/reorder`,
    { orderedIds },
  );
}

export async function addLesson(levelId: string, body: Record<string, unknown>) {
  return apiPost<ApiOneResponse<{ lesson: TrainingLesson }>>(
    `/api/v1/admin/formations/levels/${levelId}/lessons`,
    body,
  );
}

export async function getLesson(lessonId: string) {
  return apiGet<ApiOneResponse<{ lesson: TrainingLesson }>>(
    `/api/v1/admin/formations/lessons/${lessonId}`,
  );
}

export async function updateLesson(lessonId: string, body: Record<string, unknown>) {
  return apiPatch<ApiOneResponse<{ lesson: TrainingLesson }>>(
    `/api/v1/admin/formations/lessons/${lessonId}`,
    body,
  );
}

export async function appendLessonDocuments(
  lessonId: string,
  documents: Array<{ documentId: string; documentCode?: string }>,
) {
  return apiPost<ApiOneResponse<{ lesson: TrainingLesson }>>(
    `/api/v1/admin/formations/lessons/${lessonId}/documents`,
    { documents },
  );
}

export async function reorderLessonDocuments(lessonId: string, orderedIds: string[]) {
  return apiPatch<ApiOneResponse<{ lesson: TrainingLesson }>>(
    `/api/v1/admin/formations/lessons/${lessonId}/documents/reorder`,
    { orderedIds },
  );
}

export async function deleteLesson(lessonId: string) {
  return apiDelete<ApiOneResponse<{ deleted: boolean }>>(
    `/api/v1/admin/formations/lessons/${lessonId}`,
  );
}

export async function validateFormation(formationId: string) {
  return apiPost<ApiOneResponse<{ valid: boolean; errors: string[] }>>(
    `/api/v1/admin/formations/${formationId}/validate`,
  );
}

export async function publishFormation(formationId: string) {
  return apiPost<ApiOneResponse<{ program: TrainingProgramSummary }>>(
    `/api/v1/admin/formations/${formationId}/publish`,
  );
}

export type TrainingEnrolmentRow = {
  _id: string;
  userId: string;
  status: string;
  percentComplete: number;
  user?: { nickname?: string; email?: string } | null;
};

export async function listEnrolments(formationId: string) {
  return apiGet<ApiOneResponse<{ enrolments: TrainingEnrolmentRow[]; total: number }>>(
    `/api/v1/admin/formations/${formationId}/enrolments`,
  );
}

export async function assignEnrolment(formationId: string, userId: string) {
  return apiPost<ApiOneResponse<{ enrolment: TrainingEnrolmentRow }>>(
    `/api/v1/admin/formations/${formationId}/enrolments`,
    { userId },
  );
}

export async function removeLessonDocument(lessonId: string, documentSubId: string, lesson: TrainingLesson) {
  const documents = lesson.documents.filter((d) => d._id !== documentSubId);
  return updateLesson(lessonId, { documents });
}
